import { Button, InfoTooltip, Text } from '@/components/DesignSystem';
import { UserAccountQueryEndpoint } from '@api/endpoints/UserAccountQueryEndpoint';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import { Autocomplete, Box, IconButton, MenuItem, Stack, TextField } from '@mui/material';
import {
  QueryFilterConnector,
  QueryFilterOperator,
  valuelessQueryFilterOperators,
} from '@shared/enums';
import { isEqual } from 'lodash';
import { ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Column = UserAccountQueryEndpoint.Query.UserAccountQueryColumn;
type Filter = UserAccountQueryEndpoint.UserAccountQueryFilter;

interface DraftFilter {
  column: string | null,
  connector: QueryFilterConnector,
  key: number,
  operator: QueryFilterOperator,
  value: string,
}

const operatorOrder = [
  QueryFilterOperator.Equals,
  QueryFilterOperator.NotEquals,
  QueryFilterOperator.Like,
  QueryFilterOperator.NotLike,
  QueryFilterOperator.LessThan,
  QueryFilterOperator.LessThanOrEqual,
  QueryFilterOperator.GreaterThan,
  QueryFilterOperator.GreaterThanOrEqual,
  QueryFilterOperator.In,
  QueryFilterOperator.IsNull,
  QueryFilterOperator.IsNotNull,
];

const connectorWidth = 80;

// Wide enough for the longest column name and operator label.
const columnWidth = 380;
const operatorWidth = 240;

const toRequestFilters = (drafts: DraftFilter[]): Filter[] => drafts
  .filter((draft): draft is DraftFilter & { column: string } => !!draft.column)
  .map(({ column, connector, operator, value }, index) => ({
    column,
    connector: index === 0 ? undefined : connector,
    operator,
    value: valuelessQueryFilterOperators.includes(operator) ? undefined : value,
  }));

interface UserAccountFiltersProps {
  columns: Column[],
  onApply: (filters: Filter[]) => void,
  /** Sits at the right of the toolbar row. */
  toolbarEnd?: ReactNode,
  /** Sits at the left of the toolbar row, before the filter buttons. */
  toolbarStart?: ReactNode,
}

export const UserAccountFilters = ({
  columns,
  onApply,
  toolbarEnd,
  toolbarStart,
}: UserAccountFiltersProps) => {
  const { t } = useTranslation();
  const nextKey = useRef(0);
  const [drafts, setDrafts] = useState<DraftFilter[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<Filter[]>([]);
  // The rows an Apply was refused for, so rows added afterwards don't start out flagged.
  const [rowKeysMissingColumn, setRowKeysMissingColumn] = useState<Set<number>>(new Set());
  // Removing every row still leaves the applied filters to clear.
  const showsFilterActions = drafts.length > 0 || appliedFilters.length > 0;
  const hasMissingColumn = drafts.some((draft) => !draft.column);
  // Edits, including a removed row, only take effect on Apply, so say when the table lags them.
  const hasUnappliedChanges = hasMissingColumn
    || !isEqual(toRequestFilters(drafts), appliedFilters);

  const columnTypes = new Map(columns.map(({ name, type }) => [name, type]));
  const columnNames = [...columnTypes.keys()].sort((a, b) => a.localeCompare(b));

  const onAdd = () => {
    setDrafts((previous) => [...previous, {
      column: null,
      connector: QueryFilterConnector.And,
      key: nextKey.current++,
      operator: QueryFilterOperator.Equals,
      value: '',
    }]);
  };

  const onUpdate = (key: number, changes: Partial<DraftFilter>) => {
    setDrafts((previous) => previous.map((draft) => (
      draft.key === key ? { ...draft, ...changes } : draft
    )));
  };

  const onRemove = (key: number) => {
    setDrafts((previous) => previous.filter((draft) => draft.key !== key));
  };

  const onApplyDrafts = () => {
    setRowKeysMissingColumn(new Set(
      drafts.filter((draft) => !draft.column).map(({ key }) => key),
    ));
    if (hasMissingColumn) {
      return;
    }
    const requestFilters = toRequestFilters(drafts);
    setAppliedFilters(requestFilters);
    onApply(requestFilters);
  };

  const onClear = () => {
    setDrafts([]);
    setAppliedFilters([]);
    setRowKeysMissingColumn(new Set());
    onApply([]);
  };

  return (
    <Stack spacing={1}>
      <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1} useFlexGap>
        {toolbarStart}
        <Button
          onClick={onAdd}
          startIcon={<Add />}
          testID="user-accounts-filter-add"
          variant="outlined"
        >
          {t($ => $.userAccounts.addFilter)}
        </Button>
        {showsFilterActions && (
          <>
            <Button onClick={onApplyDrafts} testID="user-accounts-filter-apply">
              {t($ => $.userAccounts.applyFilters)}
            </Button>
            <Button onClick={onClear} testID="user-accounts-filter-clear" variant="text">
              {t($ => $.userAccounts.clearFilters)}
            </Button>
            <InfoTooltip placement="right">{t($ => $.userAccounts.valueHint)}</InfoTooltip>
            {hasUnappliedChanges && (
              <Text
                category="body-small"
                sx={{ color: 'warning.main' }}
                testID="user-accounts-filter-not-applied"
              >
                {t($ => $.userAccounts.notApplied)}
              </Text>
            )}
          </>
        )}
        <Box flexGrow={1} />
        {toolbarEnd}
      </Stack>
      {drafts.map((draft, index) => (
        <Stack
          alignItems="center"
          direction="row"
          flexWrap="wrap"
          gap={1}
          key={draft.key}
          useFlexGap
        >
          <Box sx={{ width: connectorWidth }}>
            {index > 0 && (
              <TextField
                data-testid={`user-accounts-filter-connector-${index}`}
                onChange={(event) => onUpdate(draft.key, {
                  connector: event.target.value as QueryFilterConnector,
                })}
                select
                size="small"
                value={draft.connector}
              >
                {Object.values(QueryFilterConnector).map((connector) => (
                  <MenuItem key={connector} value={connector}>
                    {t($ => $.userAccounts.connectors[connector])}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>
          <Autocomplete
            onChange={(_, column) => onUpdate(draft.key, { column })}
            options={columnNames}
            renderInput={(params) => (
              <TextField
                {...params}
                data-testid={`user-accounts-filter-column-${index}`}
                error={rowKeysMissingColumn.has(draft.key) && !draft.column}
                label={t($ => $.userAccounts.column)}
              />
            )}
            renderOption={(props, name) => (
              <li {...props} key={name}>
                <Stack direction="row" justifyContent="space-between" width="100%">
                  <span>{name}</span>
                  <Text category="body-small" sx={{ color: 'text.secondary' }}>
                    {columnTypes.get(name)}
                  </Text>
                </Stack>
              </li>
            )}
            size="small"
            sx={{ width: columnWidth }}
            value={draft.column}
          />
          <TextField
            data-testid={`user-accounts-filter-operator-${index}`}
            label={t($ => $.userAccounts.operator)}
            onChange={(event) => onUpdate(draft.key, {
              operator: event.target.value as QueryFilterOperator,
            })}
            select
            size="small"
            sx={{ width: operatorWidth }}
            value={draft.operator}
          >
            {operatorOrder.map((operator) => (
              <MenuItem key={operator} value={operator}>
                {t($ => $.userAccounts.operators[operator])}
              </MenuItem>
            ))}
          </TextField>
          {!valuelessQueryFilterOperators.includes(draft.operator) && (
            <TextField
              inputProps={{ 'data-testid': `user-accounts-filter-value-${index}` }}
              label={t($ => $.userAccounts.value)}
              onChange={(event) => onUpdate(draft.key, { value: event.target.value })}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  onApplyDrafts();
                }
              }}
              size="small"
              sx={{ flexGrow: 1, maxWidth: 480, minWidth: 240 }}
              value={draft.value}
            />
          )}
          <IconButton
            aria-label={t($ => $.userAccounts.removeFilter)}
            data-testid={`user-accounts-filter-remove-${index}`}
            onClick={() => onRemove(draft.key)}
            size="small"
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      ))}
    </Stack>
  );
};
